/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module CollectionsModule */

import { ApplicationProfileType } from 'generated-types';
import { GSLBVSItem } from 'ng/modules/items/factories/gslb-vs.item.factory';
import { VirtualServiceCollection } from './virtual-service.collection.factory';

/**
 * Type of GSLBVSCollection.
 */
export type TGSLBVSCollection = typeof GSLBVSCollection;

interface IGslbVsCollectionArgs {
    gslbSiteId?: string;
    gslbTenant?: string;
    isLocalVSCollection?: boolean,
    limit?: number;
    params?: object;
    hostAllSubdomainsDnsVsUuids?: string[];
    siteDnsVsUuids?: string[];
}

/**
 * @description
 *
 *     Syntax sugar to set GSLB specific headers through constructor params.
 *     Loads VSes of local as well as remote controllers.
 *
 * @author Ram Pal, alextsg
 */
export class GSLBVSCollection extends VirtualServiceCollection {
    public static ngDependencies = [
        GSLBVSItem,
    ];

    /**
     * Used to check if a GSLB VS currently hosts all subdomains in the GSLB site.
     */
    public readonly hostAllSubdomainsDnsVsUuidsSet: Set<string>;

    /**
     * Used to check if a GSLB VS is configured in a GSLB site.
     */
    public readonly siteDnsVsUuidsSet: Set<string>;

    private readonly isLocalVSCollection: boolean;

    constructor(args: IGslbVsCollectionArgs = {}) {
        const extendedArgs = {
            isStatic: true,
            objectName: 'virtualservice',
            ...args,
        };

        super(extendedArgs);

        const {
            gslbSiteId,
            gslbTenant,
            isLocalVSCollection,
            hostAllSubdomainsDnsVsUuids,
            siteDnsVsUuids,
        } = args;

        this.itemClass_ = this.getNgDependency(GSLBVSItem);

        this.isLocalVSCollection = Boolean(isLocalVSCollection);

        const headerParams = this.getParams('headers_') || {};

        if (gslbSiteId) {
            headerParams['X-Avi-Internal-GSLB'] = gslbSiteId;
        }

        if (gslbTenant) {
            headerParams['X-Avi-Tenant'] = gslbTenant;
        }

        this.setParams({ headers_: headerParams });

        if (hostAllSubdomainsDnsVsUuids) {
            this.hostAllSubdomainsDnsVsUuidsSet = new Set(hostAllSubdomainsDnsVsUuids);
        }

        if (siteDnsVsUuids) {
            this.siteDnsVsUuidsSet = new Set(siteDnsVsUuids);
        }
    }

    /** @override */
    public isCreatable(): boolean {
        if (this.isLocalVSCollection) {
            return true;
        }

        return false;
    }

    /**
     * @override
     * Create a new GSLBVSItem with arguments like hostAllSubdomainsDnsVsUuids and siteDnsVsUuids which are used to
     * modify the name.
     */
    public getNewItem(args: any): GSLBVSItem {
        if (this.hostAllSubdomainsDnsVsUuidsSet) {
            args.hostAllSubdomainsDnsVsUuids = [...this.hostAllSubdomainsDnsVsUuidsSet];
        }

        if (this.siteDnsVsUuidsSet) {
            args.siteDnsVsUuids = [...this.siteDnsVsUuidsSet];
        }

        const headerParams = this.getParams('headers_') || {};

        return super.getNewItem({
            ...args,
            params: {
                fields: 'name,tenant_ref',
                'application_profile_ref.type': ApplicationProfileType.APPLICATION_PROFILE_TYPE_DNS,
                headers_: { ...headerParams },
                ...args.params,
            },
        });
    }
}
